// Content spine for primepathmotors.com.
// Every customer-facing string below is lifted from the approved content pack:
// CLIENTS/PRIMEPATH/BRAND-CONTENT-PACK/CURRENT/03-WEBSITE-CONTENT-V2.md
// Sections referenced inline. Do not reword without a new approved version.

export const business = {
  name: "Prime Path Motors",
  tagline: "Drive Your Prime Path",
  phoneDisplay: "469-960-3446",
  phoneHref: "+14699603446",
  email: "info@primepathmotors.com",
  addressLine1: "4580 JD Mouser Pkwy, Unit 112",
  addressLine2: "Alvarado, TX 76009",
  hours: ["Mon-Fri: 9:00 AM - 6:00 PM", "Sat: 10:00 AM - 4:00 PM", "Sun: Closed"],
  legalLine: "Licensed Texas Independent Dealer",
};

// V2 section 1. Nav order exactly as approved. Book Service is a gold button, not a nav link.
export const primaryNav = [
  { label: "Home", to: "/" },
  { label: "Find Your Vehicle", to: "/find-your-vehicle" },
  { label: "Auto Services", to: "/auto-services" },
  { label: "Tires", to: "/tires" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

// Every request has its own page. A CTA that says "Book Service" opens a booking
// page; it does not scroll the reader down a page about something else. The old
// in-page anchors (#book-service, #tire-quote, #vehicle-request) are gone.
export const bookServicePath = "/book-service";
export const vehicleRequestPath = "/vehicle-request";
export const tireQuotePath = "/tire-quote";
export const appraisalPath = "/appraisal";

// Footer only. These are real destinations, they are just not top-level nav.
export const secondaryNav = [
  { label: "Book Service", to: bookServicePath },
  { label: "Trade Appraisal", to: appraisalPath },
  { label: "Tire Quote", to: tireQuotePath },
  { label: "Vehicle Request", to: vehicleRequestPath },
];

// Breadcrumb trail per route. Home is prepended by the component, so every entry
// below is the path from Home down to the current page.
export const breadcrumbs = {
  "/find-your-vehicle": [["Find Your Vehicle", "/find-your-vehicle"]],
  "/vehicle-request": [["Find Your Vehicle", "/find-your-vehicle"], ["Start Your Search", vehicleRequestPath]],
  "/auto-services": [["Auto Services", "/auto-services"]],
  "/book-service": [["Auto Services", "/auto-services"], ["Book Service", bookServicePath]],
  "/tires": [["Tires", "/tires"]],
  "/tire-quote": [["Tires", "/tires"], ["Tire Quote", tireQuotePath]],
  "/appraisal": [["Trade Appraisal", appraisalPath]],
  "/about": [["About", "/about"]],
  "/contact": [["Contact", "/contact"]],
  "/privacy": [["Privacy Notice", "/privacy"]],
  "/terms": [["Website Terms", "/terms"]],
};

// Opening hours as data, so the booking calendar, the footer and the structured
// data all read the same source. Index 0 is Sunday. null means closed.
// Times are the shop clock in Alvarado, Texas.
export const openingHours = [
  null,              // Sunday
  ["09:00", "18:00"],
  ["09:00", "18:00"],
  ["09:00", "18:00"],
  ["09:00", "18:00"],
  ["09:00", "18:00"],
  ["10:00", "16:00"], // Saturday
];
export const shopTimeZone = "America/Chicago";

// Registered handles per the approved brand content pack: primepathmotorstx
// everywhere, primepathtx on X. Order is the order they appear in the footer.
export const socialProfiles = [
  { label: "Facebook", handle: "@primepathmotorstx", url: "https://www.facebook.com/primepathmotorstx" },
  { label: "Instagram", handle: "@primepathmotorstx", url: "https://www.instagram.com/primepathmotorstx" },
  { label: "YouTube", handle: "@primepathmotorstx", url: "https://www.youtube.com/@primepathmotorstx" },
  { label: "TikTok", handle: "@primepathmotorstx", url: "https://www.tiktok.com/@primepathmotorstx" },
  { label: "LinkedIn", handle: "primepathmotorstx", url: "https://www.linkedin.com/company/primepathmotorstx" },
  { label: "X", handle: "@primepathtx", url: "https://x.com/primepathtx" },
];

// V2 section 2, three-door band under the hero.
// One label, one destination, everywhere on the site. Each CTA below is an
// action, so each one opens the request it names rather than a page about it.
export const businessLanes = [
  {
    number: "01",
    title: "Your Next Vehicle",
    copy: "Tell us what you want and what you want to spend. We source it, inspect it, recondition it, and deliver it to your door, anywhere in the country.",
    cta: "Start Your Search",
    to: vehicleRequestPath,
  },
  {
    number: "02",
    title: "Service You Can See",
    copy: "Appointment-based automotive service with written estimates before any work begins, and photos of what we find.",
    cta: "Book Service",
    to: bookServicePath,
  },
  {
    number: "03",
    title: "Tires, Quoted for Your Vehicle",
    copy: "Tell us what you drive and how you drive it. We quote the right tires at the right price, and installation can be arranged.",
    cta: "Get a Tire Quote",
    to: tireQuotePath,
  },
];

// V2 section 2. Steps 01, 02 and 04 kept from the current site. Step 03 replaces the
// line about financing options (V2 section 10, financing removal).
export const sourcingSteps = [
  ["01", "Tell Us What You Need", "Submit your vehicle preferences, budget, and requirements through our secure application. We will search our network to find your perfect match."],
  ["02", "Expert Acquisition", "Our team locates and thoroughly inspects vehicles meeting your criteria. Every vehicle undergoes rigorous evaluation before consideration."],
  ["03", "Transparent Review Process", "We provide complete vehicle details, inspection reports, and clear pricing. Take the time to review everything before you make your decision."],
  ["04", "Seamless Completion", "We handle all paperwork and documentation. Your vehicle is professionally transported directly to your location."],
];

// V2 section 4, how service works.
export const serviceSteps = [
  ["01", "Book Your Appointment", "Pick your service and time online, or call us. Appointment-based means your vehicle gets worked on when we said it would."],
  ["02", "We Inspect and Document", "We assess your vehicle and document what we find, with photos. You see what we see."],
  ["03", "You Approve First", "A written estimate before any work begins. No surprises on the invoice, no work you did not approve."],
  ["04", "Done Right, Shown Right", "We complete the approved work, return your old parts on request, and walk you through exactly what was done."],
];

// V2 section 4, What We Service. No prices published; quotes are per vehicle.
export const serviceCategories = [
  ["01", "Brake Service", "Pads, rotors, and brake inspections. The most common repair on the road, done with quality parts and documented work."],
  ["02", "Batteries and Charging", "Battery testing and replacement, alternator and starter diagnosis and replacement."],
  ["03", "Engine Diagnostics", "Check engine lights, drivability issues, and pre-purchase inspections, diagnosed with professional scan equipment and explained in plain language."],
  ["04", "Cooling System", "Radiators, hoses, thermostats, and water pumps, caught before they leave you on the shoulder."],
  ["05", "Suspension and Steering", "Struts, shocks, and CV axles for a ride that feels the way it should."],
  ["06", "Wheel Bearings", "Diagnosis and replacement of the hum you have been turning the radio up to ignore."],
];

export const serviceArea = "Serving Alvarado, Cleburne, Burleson, Mansfield, Crowley, Arlington, and the greater Fort Worth area, by appointment.";

// V2 section 5, tires value band.
export const tireValues = [
  ["01", "Quoted for Your Vehicle", "Not a wall of 400 tires. We match fitments for your exact year, make, model, and driving, and quote you clear options: good, better, best."],
  ["02", "Major Brands", "Passenger, truck, and SUV fitments across major brands, from daily commuters to work trucks."],
  ["03", "Installation Arranged", "Professional installation can be arranged with your purchase. One conversation, handled."],
];

// V2 section 7, three principles kept from the current site.
export const principles = [
  ["01", "Quality Assured", "Every vehicle undergoes rigorous inspection and reconditioning to meet our exacting standards before reaching our showroom."],
  ["02", "Transparent Process", "No games, no pressure. We believe in honest communication and building lasting relationships with our customers."],
  ["03", "Premium Selection", "We carefully curate our inventory to offer only the finest pre-owned vehicles that meet our strict criteria for quality and value."],
];

// V2 section 7, quality assurance band kept from the current site.
export const qualityAssurance = [
  ["01", "Multi-Point Vehicle Inspection", "Thorough mechanical and cosmetic assessment by experienced technicians."],
  ["02", "Reconditioning and Preparation", "Professional detailing and necessary repairs to meet our standards."],
  ["03", "Documentation Review", "Complete history verification and title documentation."],
  ["04", "Final Quality Check", "Comprehensive review before delivery approval."],
];

// V2 section 9. Exactly what Google shows. Replaces the four places in the old code
// that still described in-house financing.
export const seo = {
  home: {
    title: "Pre-Owned Vehicles, Service and Tires | Prime Path Motors",
    description: "Premium pre-owned vehicles sourced to your spec and delivered nationwide. Automotive service by appointment and tire sales in Alvarado, TX.",
  },
  vehicle: {
    title: "Find Your Vehicle | Nationwide Vehicle Sourcing | Prime Path Motors",
    description: "Tell us the vehicle, budget, and requirements. We source, inspect, and deliver it to your door, anywhere in the country.",
  },
  services: {
    title: "Auto Repair by Appointment in Alvarado, TX | Prime Path Motors",
    description: "Brakes, batteries, diagnostics, suspension and more. Written estimates before work begins and photos of what we find. Book online.",
  },
  tires: {
    title: "Tire Sales in Alvarado, TX | Prime Path Motors",
    description: "Tires quoted for your exact vehicle across major brands. Clear pricing, installation can be arranged. Get your quote.",
  },
  about: {
    title: "About Prime Path Motors | Licensed Texas Independent Dealer",
    description: "The Prime Path difference: transparent process, quality assured, premium selection.",
  },
  contact: {
    title: "Contact | Prime Path Motors, Alvarado TX",
    description: "Call 469-960-3446, visit us in Alvarado, or book service online.",
  },
  appraisal: {
    title: "Trade Appraisal | Prime Path Motors, Alvarado TX",
    description: "Tell us what you are driving now. We look at the actual vehicle, its mileage, and its condition, and come back with a number we can stand behind.",
  },
  bookService: {
    title: "Book a Service Appointment | Prime Path Motors, Alvarado TX",
    description: "Pick your date and time and tell us what the vehicle is doing. Written estimates before any work begins, and photos of what we find.",
    image: "/share-card-service.png",
  },
  vehicleRequest: {
    title: "Start Your Vehicle Search | Prime Path Motors",
    description: "Tell us the vehicle, the budget, and what you will not compromise on. We source it, inspect it, and deliver it to your door.",
  },
  tireQuote: {
    title: "Get a Tire Quote | Prime Path Motors, Alvarado TX",
    description: "Tell us what you drive and how you drive it. We come back with tire options and clear pricing. No obligation.",
    image: "/share-card-tires.png",
  },
  privacy: {
    title: "Privacy Notice | Prime Path Motors",
    description: "How Prime Path Motors uses the information you submit through this website.",
  },
  terms: {
    title: "Website Terms | Prime Path Motors",
    description: "Terms covering the information published on the Prime Path Motors website.",
  },
};

// Section share images. A page without an entry falls back to the default card.
seo.services.image = "/share-card-service.png";
seo.tires.image = "/share-card-tires.png";

// Social tags are NOT the SEO tags. A search result gives a title roughly 60
// characters of room and a link unfurl gives less, while og:site_name already
// prints the brand, so repeating it there spends the visible characters on a
// word the reader can already see. Measured on the live homepage before this:
// og:title 86 characters and og:description 139, both cut off mid-word.
// Anything without an entry here falls back to its SEO pair.
const SOCIAL = {
  home: ["Drive Your Prime Path",
    "Pre-owned vehicles sourced to your spec and delivered nationwide. Service and tires in Alvarado, TX."],
  vehicle: ["Find Your Vehicle",
    "Tell us the vehicle and the budget. We source it, inspect it and deliver it to your door."],
  vehicleRequest: ["Start Your Vehicle Search",
    "Set the number, tell us what you will not compromise on, and we source against it."],
  services: ["Service You Can See",
    "Auto repair by appointment. Written estimates before any work, and photos of what we find."],
  bookService: ["Book a Service Appointment",
    "Pick a date and time and tell us what the vehicle is doing. We confirm before it is on the board."],
  tires: ["Tires, Quoted for Your Vehicle",
    "Tires quoted for your exact vehicle across major brands. Clear pricing, installation arranged."],
  tireQuote: ["Get a Tire Quote",
    "Tell us what you drive and how you drive it. We come back with options and clear pricing."],
  appraisal: ["What Is Your Trade Worth?",
    "We look at the actual vehicle, its mileage and its condition, then come back with a number."],
  about: ["The Prime Path Difference",
    "Licensed Texas independent dealer. Transparent process, quality assured, premium selection."],
  contact: ["Talk to Us",
    "Call 469-960-3446, visit us in Alvarado, or book service online."],
  privacy: ["Privacy Notice",
    "How Prime Path Motors uses the information you submit through this website."],
  terms: ["Website Terms",
    "Terms covering the information published on the Prime Path Motors website."],
};
Object.entries(SOCIAL).forEach(([key, [title, description]]) => {
  if (seo[key]) seo[key].social = { title, description };
});

export const seoKeywords = "pre-owned vehicles, used cars Texas, vehicle sourcing, nationwide vehicle delivery, trade-in, auto repair Alvarado TX, tires Alvarado TX, Alvarado dealership";
